import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useRouteMatch } from 'react-router-dom'

import RunoutPeriodDropdown from 'adminDashboard/benefitDetails/common/RunoutPeriodDropdown'
import ProductClassWizardModal from 'adminDashboard/benefitDetails/common/productClassWizardModal'
import { ProductClassModalStateProps } from 'adminDashboard/benefitDetails/design/groupHra/classDesign'
import { BenefitDesignStep } from 'adminDashboard/benefitDetails/design/ichra/DesignStepsMapping'
import {
  IchraProductClassWizard,
  IchraProductClassWizardNavItemsMapping,
} from 'adminDashboard/benefitDetails/design/ichra/finishBenefitDesign/productClassWizardModal/ProductClassStepMapping'
import {
  IchraRenewalProductClassWizard,
  IchraRenewalProductClassWizardNavItemsMapping,
} from 'adminDashboard/benefitDetails/renewal/inchra/productClassWizardModal/ProductRenewalClassStepMapping'

import {
  BenefitDetailsResponse,
  BenefitProductClassResponse,
  RenewalBenefitDesignWizardResponse,
  addProductClassDesignWizard,
  deleteIchraProductClass,
  getBenefitDetails,
  getBenefitProductClasses,
  getIchraClassDesignWizard,
  getIchraClassDesignWizardState,
  getIchraProductClassDesignWizard,
  getIchraProductClassDesignWizardState,
  getRenewalPlanPeriod,
  removeIncompleteProductClass,
  renewalBenefitDesignWizard,
  setIchraConfirmRenewalBenefitClasses,
  setPlanPeriodRunoutDays,
} from 'core'
import benefitPlanPeriods, { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'
import { BenefitDesignStatus } from 'core/domain/benefit/benefitDesignStatus'

import {
  BenefitPlanPeriodDropDown,
  Button,
  Card,
  Col,
  DeleteConfirmModal,
  Divider,
  Dropdown,
  Menu,
  MenuItem,
  Row,
  SimpleSelect,
  Skeleton,
} from 'common/components'
import { formatYear, parseZoneFormat } from 'common/dateFormatters'
import { ROUTE_PATHS } from 'common/routePaths'

import { useCommandRequestV2, useQueryParams, useQueryRequestV2 } from '../../../../../common/hooks'
import ProductClassList from '../../../common/ProductClassList'
import SummaryEmpty from '../../SummaryEmpty'
import SummaryTitle from '../../SummaryTitle'

const IchraDesignBenefitSummary = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const queryParams = useQueryParams()
  const [productClassModalState, setProductClassModalState] = useState<ProductClassModalStateProps>({
    visible: false,
  })
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto | null>()

  const [deleteProductClass, setDeleteProductClass] = useState<BenefitProductClassResponse | null>(null)

  const { data: planPeriods } = useQueryRequestV2<PlanPeriodDto[]>({
    initialState: null,
    queryFn: () => benefitPlanPeriods(),
  })
  const {
    data: planPeriodDetails,
    isLoading: isPlanPeriodDetailsLoading,
    sendQuery: sendGetPlanPeriodDetails,
  } = useQueryRequestV2<BenefitDetailsResponse>({
    initialState: null,
    queryFn: () => getBenefitDetails(selectedPlanPeriod?.id),
    enable: !!selectedPlanPeriod,
  })

  const { data: renewalPlanPeriod } = useQueryRequestV2({ initialState: null, queryFn: () => getRenewalPlanPeriod() })

  const isRenewal = !!renewalPlanPeriod && !!planPeriodDetails && renewalPlanPeriod?.id === planPeriodDetails?.id

  const { sendCommand: sendAddProductClassDesignWizard } = useCommandRequestV2((planPeriod: string) =>
    addProductClassDesignWizard(planPeriod),
  )
  const { sendCommand: sendDeleteProductClass, isLoading: isDeleteProductClassLoading } = useCommandRequestV2(
    (productClassId: string) => deleteIchraProductClass(productClassId),
  )

  const { sendCommand: sendIchraConfirmRenewalBenefitClasses } = useCommandRequestV2((planPeriod: string) =>
    setIchraConfirmRenewalBenefitClasses(planPeriod),
  )

  const { sendCommand: sendPlanPeriodRunoutDays } = useCommandRequestV2((runOutDays: number) =>
    setPlanPeriodRunoutDays({
      planPeriodId: selectedPlanPeriod?.id,
      runOutDays,
    }),
  )

  const { sendCommand: sendRemoveIncompleteProductClass } = useCommandRequestV2((productClassId: string) =>
    removeIncompleteProductClass(productClassId),
  )

  const { data: productClassesInfo, reload: reloadProductClassesInfo } =
    useQueryRequestV2<RenewalBenefitDesignWizardResponse>({
      initialState: null,
      queryFn: () => renewalBenefitDesignWizard(),
      enable: isRenewal,
    })

  const planPeriodIdQueryParam = queryParams.get('planPeriodId')

  const {
    data: productClasses,
    sendQuery: sendGetProductClasses,
    isLoading: isProductClassesLoading,
    reload: reloadProductClasses,
  } = useQueryRequestV2<BenefitProductClassResponse[]>({
    initialState: null,
    queryFn: () => getBenefitProductClasses(selectedPlanPeriod?.id),
    enable: !!selectedPlanPeriod,
  })

  const [rounoutDays, setRunoutDays] = useState<number>(60)

  const renewalYear = Number(formatYear(renewalPlanPeriod?.startDate))

  const newRenewalProductClasses =
    productClasses?.filter((x) => !productClassesInfo?.productClassesInfo.map((info) => info.id).includes(x.id)) || []

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriodIdQueryParam
        ? planPeriods.find((x) => x.id === planPeriodIdQueryParam)
        : planPeriods.find((x) => x.active == true)

      if (found) {
        setSelectedPlanPeriod(found)
      }
    }
  }, [planPeriods])

  useEffect(() => {
    if (selectedPlanPeriod) {
      ;(async () => {
        await sendGetPlanPeriodDetails()
        await sendGetProductClasses()
      })()
    }
  }, [selectedPlanPeriod])

  useEffect(() => {
    if (planPeriodDetails) {
      setRunoutDays(planPeriodDetails.runOutDays)
    }
  }, [planPeriodDetails])

  const handleBenefitStartDataEdit = () => {
    history.push(`${ROUTE_PATHS.DESIGN_BENEFIT_PATH}/${selectedPlanPeriod.id}/${BenefitDesignStep.BenefitStartDate}`)
  }

  const onPlanPeriodChanged = (planPeriodId: string) => {
    setSelectedPlanPeriod(planPeriods.find((x) => x.id === planPeriodId))
  }

  const handleBenefitDesignEdit = (item: BenefitProductClassResponse) => {
    setProductClassModalState({
      productId: item.id,
      visible: true,
      editingStep: isRenewal
        ? IchraRenewalProductClassWizard.SetSetClassTitle
        : IchraProductClassWizard.SetEligibleExpenses,
    })
  }

  const handleAddClassForCurrentBenefit = () => {
    history.push(`${ROUTE_PATHS.DESIGN_BENEFIT_PATH}/${selectedPlanPeriod.id}/${BenefitDesignStep.EmployeeClasses}`)
  }

  const handleAddClass = async () => {
    const result = await sendAddProductClassDesignWizard(selectedPlanPeriod.id)
    if (result) {
      reloadProductClasses()
      reloadProductClassesInfo()
      setProductClassModalState({
        productId: result.data.productClassId,
        visible: true,
        editingStep: isRenewal
          ? IchraRenewalProductClassWizard.SetSetClassTitle
          : IchraProductClassWizard.SetEligibleExpenses,
      })
    }
  }

  const onProductClassWizardModalClosed = async () => {
    setProductClassModalState({
      ...productClassModalState,
      visible: false,
    })
    await sendRemoveIncompleteProductClass(productClassModalState.productId)
    reloadProductClasses()
    reloadProductClassesInfo()
  }

  const onDeleteProductClass = async () => {
    const result = await sendDeleteProductClass(deleteProductClass?.id)
    if (result.completed) {
      setDeleteProductClass(null)
      await reloadProductClasses()
    }
  }

  return (
    <Card
      title={
        <Row justify={'space-between'} className={'w-100'}>
          <Col>
            <SummaryTitle />
          </Col>
          <Col>
            {planPeriods && (
              <BenefitPlanPeriodDropDown
                defaultValue={planPeriodIdQueryParam}
                planPeriods={planPeriods}
                onPlanPeriodSelected={onPlanPeriodChanged}
              />
            )}
          </Col>
        </Row>
      }
      className="benefit_details__summary"
    >
      <Skeleton active loading={isPlanPeriodDetailsLoading} paragraph={{ rows: 10 }}>
        {planPeriodDetails && (
          <>
            {planPeriodDetails.benefitDesignStatus === BenefitDesignStatus.Completed ||
            planPeriodDetails.benefitDesignStatus === BenefitDesignStatus.Signed ? (
              <>
                {!isRenewal && (
                  <>
                    <Row className={'mb-8'}>
                      <Col span={24}>
                        <Row className="with_card">
                          <Col span={18} className="content">
                            <h5 className="mb-0">
                              <b>{t('admin-dashboard.benefit-details.summary.start-date-title')}</b>
                            </h5>
                            <h5 className="mb-0">{parseZoneFormat(planPeriodDetails.startDate)}</h5>
                          </Col>
                          <Col span={6} className="flex justify-end">
                            <Button
                              type="link"
                              className={'t-start-date'}
                              disabled={planPeriodDetails?.benefitDesignStatus === BenefitDesignStatus.Signed}
                              onClick={handleBenefitStartDataEdit}
                            >
                              {t('admin-dashboard.benefit-details.summary.edit-button')}
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                    {planPeriodDetails.notificationDate && (
                      <Row className={'mb-8'}>
                        <Col span={24}>
                          <Row className="with_card">
                            <Col span={18} className="content">
                              <h5 className="mb-0">
                                <b>Notification Date</b>
                              </h5>
                              <h5 className="mb-0">{parseZoneFormat(planPeriodDetails.notificationDate)}</h5>
                            </Col>
                            <Col span={6} className="flex justify-end">
                              <Button
                                type="link"
                                className={'t-start-date'}
                                disabled={planPeriodDetails?.benefitDesignStatus === BenefitDesignStatus.Signed}
                                onClick={handleBenefitStartDataEdit}
                              >
                                {t('admin-dashboard.benefit-details.summary.edit-button')}
                              </Button>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
                <Skeleton loading={isProductClassesLoading}>
                  <>
                    {isRenewal && (
                      <Row className="mb-8">
                        <Col sm={24} lg={17}>
                          <h5 className="mb-16 text-bold">Confirm your benefit design details</h5>
                          <p className="color-gray-8 mb-16">
                            Your current {renewalYear - 1} ICHRA classes are listed below. By default, all of your
                            current classes are being offered for {renewalYear} with their current settings. Making
                            changes to the class list or the details of a specific class won’t impact your current{' '}
                            {renewalYear - 1} benefit.
                          </p>
                          <p className="color-gray-8 mb-0">To make changes:</p>
                          <ul className="color-gray-8">
                            <li>Clicking the down arrow will open a class’ settings display.</li>
                            <li>
                              Moving the “Offering for {renewalYear} selector from right to left will remove that class
                              from next year’s benefit.
                            </li>
                          </ul>
                        </Col>
                      </Row>
                    )}
                    {productClasses && (
                      <ProductClassList
                        productClasses={productClasses}
                        planPeriodId={planPeriodDetails.id}
                        isRenewal={isRenewal}
                        productClassesInfo={productClassesInfo}
                        onEdit={
                          planPeriodDetails.benefitDesignStatus !== BenefitDesignStatus.Signed
                            ? handleBenefitDesignEdit
                            : null
                        }
                        onRenewalEdit={handleBenefitDesignEdit}
                        onSubmit={async () => {
                          await reloadProductClasses()
                          await reloadProductClassesInfo()
                        }}
                        onDelete={
                          planPeriodDetails.benefitDesignStatus !== BenefitDesignStatus.Signed
                            ? (item) => setDeleteProductClass(item)
                            : null
                        }
                        onRenewalDelete={async (item) => {
                          await sendDeleteProductClass(item.id)
                          reloadProductClasses()
                          reloadProductClassesInfo()
                        }}
                      />
                    )}

                    <DeleteConfirmModal
                      title={`Deleting '${deleteProductClass?.name}' ...`}
                      content={`Are you sure you want to delete ${deleteProductClass?.name}? This action cannot be undone.`}
                      onDelete={onDeleteProductClass}
                      onCancel={() => {
                        setDeleteProductClass(null)
                      }}
                      deleteButtonLoading={isDeleteProductClassLoading}
                      visible={!!deleteProductClass}
                    />

                    {planPeriodDetails.benefitDesignStatus !== BenefitDesignStatus.Signed && (
                      <div className="mb-16">
                        <Button
                          className="flex flex__align_center"
                          type="default"
                          icon="fa fa-plus"
                          onClick={handleAddClassForCurrentBenefit}
                        >
                          Add new class
                        </Button>
                      </div>
                    )}

                    {isRenewal && (
                      <div className="mb-16">
                        <div className="color-gray-9 text-bold font-16 mb-16">Create new eligible classes</div>
                        <div className="color-gray-8 mb-16">
                          You may also add a new class to your 2024 benefit if it is not currently offered.
                        </div>
                        <Button
                          className="flex flex__align_center"
                          type="default"
                          icon="fa fa-plus"
                          onClick={handleAddClass}
                        >
                          {`Add new class for 2024`}
                        </Button>
                      </div>
                    )}
                    {productClasses && (
                      <RunoutPeriodDropdown
                        rounoutDays={rounoutDays}
                        onChange={(value) => {
                          setRunoutDays(value)
                          sendPlanPeriodRunoutDays(value)
                        }}
                        displayOnly={!isRenewal}
                      />
                    )}
                    {isRenewal && (
                      <div>
                        <Divider className="mb-16 mt-32 -mx-24 w-auto" />
                        <div className="flex flex__end">
                          <Button
                            type="primary"
                            onClick={async () => {
                              const result = await sendIchraConfirmRenewalBenefitClasses(selectedPlanPeriod.id)
                              if (result.completed) {
                                history.push(ROUTE_PATHS.RENEWAL_TASKS)
                              }
                            }}
                            disabled={
                              !productClassesInfo?.productClassesInfo?.some((x) => x.offer) &&
                              newRenewalProductClasses.filter((x) => x.designCompleted).length === 0
                            }
                          >
                            Submit
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                </Skeleton>
              </>
            ) : (
              <SummaryEmpty planPeriodId={selectedPlanPeriod.id} />
            )}
          </>
        )}
      </Skeleton>
      {productClassModalState.visible && (
        <ProductClassWizardModal
          navigationMapping={
            isRenewal ? IchraRenewalProductClassWizardNavItemsMapping : IchraProductClassWizardNavItemsMapping
          }
          getProductClassWizard={(_, productId) => {
            return isRenewal ? getIchraClassDesignWizard(productId) : getIchraProductClassDesignWizard(productId)
          }}
          getProductClassWizardState={(_, productId) => {
            return isRenewal
              ? getIchraClassDesignWizardState(productId)
              : getIchraProductClassDesignWizardState(productId)
          }}
          visible={productClassModalState.visible}
          id={productClassModalState.productId}
          planPeriodId={isRenewal ? renewalPlanPeriod.id : selectedPlanPeriod.id}
          afterClose={async () => {
            await onProductClassWizardModalClosed()
          }}
          onSubmit={async () => {
            await reloadProductClasses()
            await reloadProductClassesInfo()
          }}
          editingStep={productClassModalState.editingStep}
          wizardTitle="Benefit design"
        />
      )}
    </Card>
  )
}

export default IchraDesignBenefitSummary
